import { useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * Custom hook to fetch and subscribe to portal version information
 * 
 * @param {string} environment - The environment to fetch version for (dev, staging, prod)
 * @returns {object} Version information and loading state
 */
export function usePortalVersion(environment = 'dev') {
  const [version, setVersion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let unsubscribe;

    const fetchVersion = async () => {
      try {
        // Subscribe to real-time updates
        const versionRef = doc(db, 'portalVersions', 'current');
        
        unsubscribe = onSnapshot(
          versionRef,
          (snapshot) => {
            if (snapshot.exists()) {
              const data = snapshot.data();
              const envData = data[environment];
              
              if (envData) {
                setVersion({
                  version: envData.version || '7.1.2',
                  deployedAt: envData.deployedAt?.toDate?.() || new Date(),
                  deployedBy: envData.deployedBy || { name: 'Unknown', email: '' },
                  commit: envData.commit || 'unknown',
                  branch: envData.branch || 'unknown',
                  environment,
                  isRollback: envData.isRollback || false,
                  rolledBackFrom: envData.rolledBackFrom || null
                });
              } else {
                // Environment not found, use default
                setVersion({
                  version: '7.1.2',
                  deployedAt: new Date(),
                  deployedBy: { name: 'System', email: 'system@zippe.co.uk' },
                  commit: 'initial',
                  branch: 'main',
                  environment,
                  isRollback: false
                });
              }
            } else {
              // Document doesn't exist, use default
              setVersion({
                version: '7.1.2',
                deployedAt: new Date(),
                deployedBy: { name: 'System', email: 'system@zippe.co.uk' },
                commit: 'initial',
                branch: 'main',
                environment,
                isRollback: false
              });
            }
            setLoading(false);
            setError(null);
          },
          (err) => {
            console.error('Error fetching version:', err);
            setError(err.message);
            // Fallback to default version on error
            setVersion({
              version: '7.1.2',
              deployedAt: new Date(),
              environment,
              deployedBy: { name: 'Unknown', email: '' },
              commit: 'unknown',
              branch: 'unknown',
              isRollback: false
            });
            setLoading(false);
          }
        );
      } catch (err) {
        console.error('Error setting up version listener:', err);
        setError(err.message);
        // Fallback to default version
        setVersion({
          version: '7.1.2',
          deployedAt: new Date(),
          environment,
          deployedBy: { name: 'Unknown', email: '' },
          commit: 'unknown',
          branch: 'unknown',
          isRollback: false
        });
        setLoading(false);
      }
    };

    fetchVersion();

    // Cleanup subscription on unmount
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [environment]);

  return {
    version: version?.version || '7.1.2',
    fullVersion: version,
    loading,
    error,
    isRollback: version?.isRollback || false,
    deployedAt: version?.deployedAt,
    deployedBy: version?.deployedBy,
    commit: version?.commit,
    branch: version?.branch,
    environment
  };
}

/**
 * Get the current environment based on the hostname
 */
export function getCurrentEnvironment() {
  const hostname = window.location.hostname;
  
  if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
    return 'dev';
  } else if (hostname.includes('staging') || hostname.includes('stage')) {
    return 'staging';
  } else if (hostname.includes('zippedriveralpha') || hostname.includes('zippe')) {
    // Check if it's the production URL
    if (hostname.includes('-dev')) {
      return 'dev';
    } else if (hostname.includes('-staging')) {
      return 'staging';
    }
    return 'prod';
  }
  
  return 'dev'; // Default to dev
}

/**
 * Format version with environment badge
 */
export function formatVersionDisplay(version, environment) {
  const envColors = {
    dev: '#3b82f6', // blue
    staging: '#f59e0b', // amber
    prod: '#10b981' // emerald
  };
  
  const envLabels = {
    dev: 'DEV',
    staging: 'STAGING',
    prod: 'PROD'
  };
  
  return {
    version: `v${version}`,
    environment: envLabels[environment] || 'DEV',
    color: envColors[environment] || '#3b82f6'
  };
}